import { VERSION_LANGS, type CaptionCard, type Clip, type Word } from "./clip.js";

export { VERSION_LANGS };
export type VersionLang = (typeof VERSION_LANGS)[number];
export type CaptionLang = "en" | VersionLang;
export const LANG_NAMES: Record<CaptionLang, string> = { en: "English", ar: "Arabic", tr: "Turkish", ur: "Urdu", fr: "French", id: "Indonesian", so: "Somali", bs: "Bosnian" };
/** Languages written right to left (Arabic script). */
export const isRtl = (lang: string): boolean => lang === "ar" || lang === "ur";
/** Caption/opener font used for Arabic-script versions unless the clip's own font can shape them. */
export const ARABIC_FONT = "Cairo";
/** The studio's built-in default per script language (Settings can change it; a clip can override it). Naskh for Urdu: Kufi reads badly. */
export const SCRIPT_FONT: Partial<Record<CaptionLang, string>> = { ar: "Cairo", ur: "Amiri" };
/** How much larger Arabic script captions run than the English size: it sits lower and reads smaller at the same px. */
export const RTL_SIZE_SCALE = 1.15;
export interface LocaliseOpts { arabicFonts?: readonly string[]; langFonts?: Partial<Record<CaptionLang, string>> }

/** Fingerprint of everything a translation depends on: the caption cards, opener text and end line. */
export function versionFingerprint(clip: Clip): string {
  const src = [clip.captions.map((c) => `${c.id}:${c.words.map((w) => w.text).join(" ")}`).join("|"), clip.hook.text, clip.hook.subtitle, clip.endCard.line].join("\n");
  let h = 5381;
  for (let i = 0; i < src.length; i++) h = ((h * 33) ^ src.charCodeAt(i)) >>> 0;
  return h.toString(16);
}

/** True when the English edit changed after this language was translated. */
export const versionStale = (clip: Clip, lang: VersionLang): boolean => { const v = clip.versions[lang]; return !!v && v.source !== versionFingerprint(clip); };

/** Spreads translated text across a card's time span, longer words taking a little longer. */
export function spreadEven(text: string, startMs: number, endMs: number): Word[] {
  const toks = text.split(/\s+/).filter(Boolean);
  if (!toks.length) return [];
  const span = Math.max(toks.length * 120, endMs - startMs);
  const weights = toks.map((t) => Math.max(2, t.replace(/[^\p{L}\p{N}]/gu, "").length + 1));
  const total = weights.reduce((a, b) => a + b, 0);
  let cur = startMs;
  return toks.map((t, i) => { const d = (span * weights[i]) / total; const w = { text: t, startMs: Math.round(cur), endMs: Math.round(cur + d) }; cur += d; return w; });
}

/** The clip as it renders in a language: translated cards, opener and end line, an Arabic-capable font and RTL where needed. English (or a missing version) returns the clip unchanged. */
export function localiseClip(clip: Clip, lang: CaptionLang, opts: LocaliseOpts = {}): Clip {
  if (lang === "en") return clip;
  const v = clip.versions[lang];
  if (!v) return clip;
  const captions: CaptionCard[] = clip.captions
    .map((c) => {
      const t = v.cards[c.id];
      if (!t || !t.text.trim()) return { ...c, words: [] as Word[] };
      return { ...c, words: t.words && t.words.length ? t.words : spreadEven(t.text, c.startMs, c.endMs) };
    })
    .filter((c) => c.words.length);
  const rtl = isRtl(lang);
  // Arabic script needs a font that can shape it. The reel passes its list of Arabic-capable fonts; without it, a deliberate choice
  // (the clip's own, or the studio default: both come from pickers that only offer capable fonts) is trusted, and only the clip's
  // everyday font is checked against the built-ins.
  const known = opts.arabicFonts;
  const chosenOk = (font: string) => !rtl || !known || known.includes(font);
  const ownOk = (font: string) => !rtl || (known ?? [ARABIC_FONT, ...Object.values(SCRIPT_FONT)]).includes(font);
  const own = clip.langStyle[lang];
  const house = opts.langFonts?.[lang];
  const scriptFallback = SCRIPT_FONT[lang] ?? ARABIC_FONT;
  // the clip's own choice for this language wins, then the studio default, then the clip's font when it can shape the script, then the built-in
  const pick = (font: string): string => {
    if (own?.font && chosenOk(own.font)) return own.font;
    if (house && chosenOk(house)) return house;
    if (!rtl) return font;
    return ownOk(font) ? font : scriptFallback;
  };
  const scale = own?.sizeScale ?? (rtl ? RTL_SIZE_SCALE : 1);
  // a language version shows its own words only: the second line belongs to the English master
  const style = { ...clip.style, font: pick(clip.style.font), fontSize: Math.round(clip.style.fontSize * scale), secondLang: null, ...(rtl ? { letterSpacing: 0, caseMode: "as-is" as const } : {}) };
  const hook = { ...clip.hook, text: v.hook.text.trim() || clip.hook.text, subtitle: v.hook.subtitle.trim() || clip.hook.subtitle, font: pick(clip.hook.font) };
  const endCard = { ...clip.endCard, line: v.endLine.trim() || clip.endCard.line, font: pick(clip.endCard.font) };
  return { ...clip, captions, style, hook, endCard, captionLang: lang };
}

/** What a card's second line says and how it is set: the chosen language's translation of that card, in a font that can shape it. Null when off, or not translated yet. */
export function secondLineFor(clip: Pick<Clip, "style" | "versions" | "langStyle" | "captionLang">, cardId: string, langFonts?: Partial<Record<string, string>>): { text: string; lang: CaptionLang; font: string; rtl: boolean } | null {
  const lang = clip.style.secondLang as CaptionLang | null;
  if (!lang || lang === clip.captionLang) return null;
  const v = (clip.versions as Partial<Record<string, { cards: Record<string, { text: string }> }>>)[lang];
  const text = v?.cards[cardId]?.text?.trim();
  if (!text) return null;
  const rtl = isRtl(lang);
  const own = (clip.langStyle as Partial<Record<string, { font: string | null }>>)[lang]?.font;
  const font = own ?? langFonts?.[lang] ?? (rtl ? SCRIPT_FONT[lang] ?? ARABIC_FONT : clip.style.font);
  return { text, lang, font, rtl };
}
